import type { Auftrag, GesamtUmfang } from '@awf/shared';
import {
  collection,
  doc,
  limit as grenzeVon,
  onSnapshot,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { initFirebase } from '../firebase.js';
import { WARTE_STATUS } from './status.js';

/**
 * `ausCache` ist nicht kosmetisch. Firestore ist offline-first: fehlt die Verbindung,
 * liefert es SOFORT aus dem lokalen Zwischenspeicher statt einen Fehler zu melden -- und
 * der ist beim ersten Seitenaufruf leer. Ohne dieses Kennzeichen kaeme eine leere Liste
 * an und ein nicht erreichbarer Dienst saehe aus wie "keine Auftraege" (Abnahmekriterium
 * 8 verbietet genau das; belegt in der Live-Abnahme vom 2026-09-10).
 */
export interface AuftragsQuelle {
  beobachte(
    uid: string,
    grenze: number,
    aufDaten: (auftraege: Auftrag[], ausCache: boolean) => void,
    aufFehler: (fehler: unknown) => void,
  ): () => void;

  /**
   * Wartende Laeufe, unabhaengig vom Alter -- bis zur uebergebenen Grenze.
   *
   * Warum das eine EIGENE Abfrage braucht: `beobachte` schneidet in Firestore nach Alter ab,
   * BEVOR der Client wartende Laeufe nach vorn stellt. Ein wartender Lauf jenseits der Grenze
   * fehlt damit vollstaendig -- und auf genau den wartet ein Mensch. Eine groessere Grenze
   * verschiebt das Problem nur; serverseitig auf den Status zu filtern beseitigt es.
   *
   * Die Grenze ist trotzdem vorhanden, nur grosszuegig. "Wartende Laeufe sammeln sich
   * fachlich nicht an" ist eine Annahme ueber das Verhalten von Menschen, keine erzwungene
   * Regel -- wird nie entschieden, wachsen sie sehr wohl, und ein unbegrenzter
   * Echtzeit-Listener waere dann ein unbegrenzter Initial-Read.
   */
  beobachteWartende(
    uid: string,
    grenze: number,
    aufDaten: (auftraege: Auftrag[], ausCache: boolean) => void,
    aufFehler: (fehler: unknown) => void,
  ): () => void;

  beobachteEinen(
    runId: string,
    aufDaten: (auftrag: Auftrag | null, ausCache: boolean) => void,
    aufFehler: (fehler: unknown) => void,
  ): () => void;

  /**
   * Gesamtansicht, neueste zuerst: `ergo` (Rolle `admin`) alle Auftraege des Mandanten `ergo`,
   * `alle` (Rolle `technik`) jeden Auftrag. Ohne eigene Wartenden-Abfrage: Wer hier liest,
   * entscheidet nicht ueber fremde Auftraege, also geht ihm kein wartender Lauf verloren.
   */
  beobachteGesamt(
    umfang: GesamtUmfang,
    grenze: number,
    aufDaten: (auftraege: Auftrag[], ausCache: boolean) => void,
    aufFehler: (fehler: unknown) => void,
  ): () => void;
}

const SAMMLUNG = 'auftraege';

/**
 * PFLICHT, nicht Feinschliff. Ohne diese Option meldet Firestore nur Ereignisse, bei denen
 * sich DATEN geaendert haben. Bestaetigt der Server genau den Stand, der schon im
 * Zwischenspeicher lag, ist `fromCache: true -> false` die einzige Aenderung -- der
 * Beobachter wird nicht gerufen. `use-runs` verwirft aber jede Aufnahme aus dem
 * Zwischenspeicher, solange es den Server noch nicht gesehen hat, und meldet nach
 * `LADE_ZEITGRENZE_MS` eine Dienststoerung, die keine ist. Genau dieser Fall tritt beim
 * zweiten Seitenaufruf ohne neue Laeufe ein, also im Alltag.
 */
const METADATEN_MITZAEHLEN = { includeMetadataChanges: true } as const;

/** Startet eine Firestore-Beobachtung, sobald die App-Initialisierung durch ist. */
function verzoegert(
  starte: (handles: Awaited<ReturnType<typeof initFirebase>>) => () => void,
  aufFehler: (fehler: unknown) => void,
): () => void {
  let abbestellen: (() => void) | null = null;
  let abgebaut = false;

  void initFirebase()
    .then((handles) => {
      if (abgebaut) return;
      abbestellen = starte(handles);
    })
    .catch(aufFehler);

  return () => {
    abgebaut = true;
    abbestellen?.();
  };
}

export function firestoreAuftragsQuelle(): AuftragsQuelle {
  return {
    beobachte(uid, grenze, aufDaten, aufFehler) {
      return verzoegert(
        ({ db }) =>
          onSnapshot(
            // Der `ownerUid`-Filter ist PFLICHT, nicht Bequemlichkeit: Firestore wertet die
            // Rules bei Listen gegen die ABFRAGE aus, nicht gegen die Treffer. Ohne ihn wird
            // die Abfrage abgewiesen (belegt gegen den Emulator, tools/rules.test.mjs).
            query(
              collection(db, SAMMLUNG),
              where('ownerUid', '==', uid),
              orderBy('angelegtAm', 'desc'),
              grenzeVon(grenze),
            ),
            METADATEN_MITZAEHLEN,
            (aufnahme) =>
              aufDaten(
                aufnahme.docs.map((d) => d.data() as Auftrag),
                aufnahme.metadata.fromCache,
              ),
            aufFehler,
          ),
        aufFehler,
      );
    },

    beobachteWartende(uid, grenze, aufDaten, aufFehler) {
      return verzoegert(
        ({ db }) =>
          onSnapshot(
            // Die Grenze ist hier unschaedlich, anders als bei `beobachte`: Dort schneidet
            // sie VOR der Priorisierung und laesst wartende Laeufe verschwinden, hier ist
            // serverseitig schon auf den Status gefiltert. Der Composite-Index dafuer steht
            // in `firestore.indexes.json` (ownerUid + status + angelegtAm); fehlt er, meldet
            // Firestore FAILED_PRECONDITION -- einer der wenigen Fehler, die das SDK wirklich
            // durchreicht, also sichtbar und nicht still.
            query(
              collection(db, SAMMLUNG),
              where('ownerUid', '==', uid),
              // Die Status, auf die ein Mensch wartet (`WARTE_STATUS`) -- sie duerfen nie aus der
              // Liste fallen. `in` nutzt denselben Composite-Index (ownerUid + status + angelegtAm).
              where('status', 'in', [...WARTE_STATUS]),
              // AUFSTEIGEND, anders als die Hauptabfrage -- und das ist der Punkt: Greift die
              // Grenze, fallen hier die NEUESTEN wartenden Laeufe heraus, nicht die aeltesten.
              // Die neuesten stehen ohnehin in der Hauptabfrage (die zuletzt angelegten
              // HOL_GRENZE). Absteigend sortiert wuerde die Grenze genau die Laeufe
              // ausschliessen, auf die am laengsten jemand wartet -- also das Gegenteil des
              // Zwecks dieser Abfrage. Gemeldet vom Gate am eigenen Zweig, 2026-09-14.
              orderBy('angelegtAm', 'asc'),
              grenzeVon(grenze),
            ),
            METADATEN_MITZAEHLEN,
            (aufnahme) =>
              aufDaten(
                aufnahme.docs.map((d) => d.data() as Auftrag),
                aufnahme.metadata.fromCache,
              ),
            aufFehler,
          ),
        aufFehler,
      );
    },

    beobachteGesamt(umfang, grenze, aufDaten, aufFehler) {
      return verzoegert(
        ({ db }) =>
          onSnapshot(
            // Fuer `ergo` ist der `mandant`-Filter PFLICHT wie `ownerUid` oben: Die Rules lassen
            // `admin` nur Auftraege mit `mandant == 'ergo'` lesen und pruefen das an der Abfrage.
            // Ohne ihn scheitert die Liste vollstaendig (tools/rules.test.mjs). Er ist zugleich der
            // Schnitt, der elaboratum-Auftraege draussen haelt. Index: mandant + angelegtAm.
            // `alle` (Rolle `technik`) haengt in den Rules nicht am Dokument und braucht keinen.
            umfang === 'ergo'
              ? query(
                  collection(db, SAMMLUNG),
                  where('mandant', '==', 'ergo'),
                  orderBy('angelegtAm', 'desc'),
                  grenzeVon(grenze),
                )
              : query(collection(db, SAMMLUNG), orderBy('angelegtAm', 'desc'), grenzeVon(grenze)),
            METADATEN_MITZAEHLEN,
            (aufnahme) =>
              aufDaten(
                aufnahme.docs.map((d) => d.data() as Auftrag),
                aufnahme.metadata.fromCache,
              ),
            aufFehler,
          ),
        aufFehler,
      );
    },

    beobachteEinen(runId, aufDaten, aufFehler) {
      return verzoegert(
        ({ db }) =>
          onSnapshot(
            doc(db, SAMMLUNG, runId),
            METADATEN_MITZAEHLEN,
            (aufnahme) =>
              aufDaten(
                aufnahme.exists() ? (aufnahme.data() as Auftrag) : null,
                aufnahme.metadata.fromCache,
              ),
            aufFehler,
          ),
        aufFehler,
      );
    },
  };
}
